<script setup lang="ts">
import type { Link } from "@nuxt/ui-pro/types";
import type { SNavButton } from "~/types/landing";

const route = useRouter().currentRoute;
const { isAuthenticated } = storeToRefs(useUserStore());

const navButton = ref<SNavButton>({
  label: isAuthenticated.value ? "Go to App" : "Get Started",
  icon: isAuthenticated.value ? "i-heroicons-square-3-stack-3d-20-solid" : "i-simple-icons-google",
  loading: false,
  click: async () => {
    navButton.value.loading = true;
    isAuthenticated.value
      ? await navigateTo("/app/user")
      : await useUserStore().signIn();
  },
});

const navItems = computed<Link[]>(() => [
  {
    label: "Why",
    to: "/#why",
    active: route.value.path === "/" && route.value.hash === "#why",
  },
  {
    label: "Features",
    to: "/#features",
    active: route.value.path === "/" && route.value.hash === "#features",
  },
  {
    label: "Pricing",
    to: "/#pricing",
    active: route.value.path === "/" && route.value.hash === "#pricing",
  },
  {
    label: "Blog",
    to: "/blog",
  },
]);

const footerItems = [{
  label: "Privacy",
  to: "/privacy",
}, {
  label: "Terms",
  to: "/terms",
}];

// const endOfCyberMonday = new Date("2024-12-02T23:59:00Z");
// const isCyberMondayPassed = computed(() => {
//   return endOfCyberMonday.getTime() < new Date().getTime();
// });
// const now = useNow();
// const timeLeft = computed(() => {
//   const diff = endOfCyberMonday.getTime() - now.value.getTime();
//   return Math.max(0, diff);
// });
// const timeLeftFormatted = computed(() => {
//   const days = Math.floor(timeLeft.value / (1000 * 60 * 60 * 24));
//   const hours = Math.floor((timeLeft.value % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
//   const minutes = Math.floor((timeLeft.value % (1000 * 60 * 60)) / (1000 * 60));
//   const seconds = Math.floor((timeLeft.value % (1000 * 60)) / 1000);

//   return [
//     { label: "Days", value: days.toString() },
//     { label: "Hours", value: hours.toString() },
//     { label: "Minutes", value: minutes.toString() },
//     { label: "Seconds", value: seconds.toString() },
//   ];
// });
</script>

<template>
  <div>
    <!-- Top Navigation -->
    <UHeader
      to="/"
      :links="navItems"
    >
      <template #logo>
        <Logo class="w-auto h-7" />
      </template>

      <template #center>
        <nav class="hidden lg:flex">
          <UHeaderLinks :links="navItems" />
        </nav>
      </template>

      <template #right>
        <UColorModeButton />
        <UButton
          variant="solid"
          size="md"
          class="ml-1"
          :leading-icon="navButton.icon"
          :loading="navButton.loading"
          @click.prevent="navButton.click"
        >
          {{ navButton.label }}
        </UButton>
      </template>
    </UHeader>

    <!-- Main Content -->
    <UMain>
      <slot />
    </UMain>

    <!-- Footer -->
    <UFooter :links="footerItems">
      <template #left>
        <p class="text-gray-500 dark:text-gray-400 text-sm">
          © {{ new Date().getFullYear() }} {{ useRuntimeConfig().public.appAuthor }}
        </p>
      </template>

      <template #right>
        <UButton
          :to="`https://x.com/${useRuntimeConfig().public.appTwitterHandle}`"
          target="_blank"
          icon="i-simple-icons-x"
          color="gray"
          variant="ghost"
        >
          <span class="sr-only">Go to Twitter Profile</span>
        </UButton>
        <UButton
          :to="`mailto:${useRuntimeConfig().public.contactEmail}`"
          target="_blank"
          icon="i-heroicons-envelope-20-solid"
          color="gray"
          variant="ghost"
        >
          <span class="sr-only">Send Email to Developer</span>
        </UButton>
      </template>
    </UFooter>
  </div>
</template>
